import { useDispatch, useSelector } from 'react-redux';
import { useFlags, useNotifications } from '@fullstory/react-hooks';
import { AppDispatch } from '../../../types';
import { useCallback } from 'react';
import { getOrgId } from '../../fullstory/selectors';
import { DefinedEventsRequestActions } from './actions';
import { DefinedEvent, SpecTargetObjectType } from '@fullstory/proto/fs/services/search-settings';
import {
  DefinedEventFilter,
  DefinedEventIssueSilence,
} from '@fullstory/proto/fs/services/dx-objects';
import { useFsSpecActions } from '../../specs';
import { intl } from '@fullstory/intl';
import { AppBackendRequestActions } from '../../appbackend';
import { Timestamp } from '@fullstory/proto/google/protobuf';

export interface DefinedEventIssueSilenceUpdate {
  prev: DefinedEventIssueSilence | null;
  next: DefinedEventIssueSilence | null;
}

export const useDefinedEventActions = () => {
  const notifications = useNotifications();
  const dispatch = useDispatch<AppDispatch>();
  const orgId = useSelector(getOrgId);
  const { trackSpecMappingUpdate, updateObjectWithFsSpec } = useFsSpecActions();
  const flags = useFlags();

  const save = useCallback(
    async (
      definedEvent: DefinedEvent,
      fsSpecSlugs: string[] | null,
      snoozed: DefinedEventIssueSilenceUpdate | null,
    ) => {
      try {
        const response = await dispatch(
          DefinedEventsRequestActions.setDefinedEvent(orgId, definedEvent, null),
        );

        const id = response.definedEvent?.definedEvent?.definedEvent?.id;

        if (flags.featureFlags().AppFsSpec && fsSpecSlugs && id) {
          const mappings = await updateObjectWithFsSpec(
            id,
            SpecTargetObjectType.SPEC_TARGET_OBJECT_TYPE_DEFINED_EVENT,
            fsSpecSlugs,
          );

          mappings.forEach(({ mapping }) => {
            if (mapping?.mapping) trackSpecMappingUpdate(mapping.mapping, 'UPDATE', 'MODAL');
          });
        }

        if (!snoozed?.prev && snoozed?.next) {
          await dispatch(
            DefinedEventsRequestActions.snoozeDefinedEvent({
              orgId,
              definedEventId: snoozed.next.definedEventId,
              expiresAt: snoozed.next.expiresAt,
            }),
          );
        } else if (snoozed?.prev && !snoozed.next) {
          await dispatch(
            DefinedEventsRequestActions.unsnoozeDefinedEvent({
              orgId,
              definedEventId: snoozed.prev.definedEventId,
              issueSilenceId: snoozed.prev.id,
            }),
          );
        }

        notifications?.success('Successfully saved Defined Event');
        return response;
      } catch (e) {
        notifications?.error('Failed to save Defined Event');
        return null;
      }
    },
    [dispatch, notifications, orgId, flags, trackSpecMappingUpdate, updateObjectWithFsSpec],
  );

  const archive = useCallback(
    async (definedEventId: string) => {
      try {
        const response = await dispatch(
          DefinedEventsRequestActions.archiveDefinedEventRequest(orgId, definedEventId, true),
        );
        notifications?.success('Successfully archived Defined Event');
        return response;
      } catch (e) {
        notifications?.error('Failed to archive Defined Event');
        return null;
      }
    },
    [dispatch, notifications, orgId],
  );

  const unarchive = useCallback(
    async (definedEventId: string) => {
      try {
        const response = await dispatch(
          DefinedEventsRequestActions.archiveDefinedEventRequest(orgId, definedEventId, false),
        );
        notifications?.success('Successfully unarchived Defined Event');
        return response;
      } catch (e) {
        notifications?.error('Failed to unarchive Defined Event');
        return null;
      }
    },
    [dispatch, notifications, orgId],
  );

  const archiveAll =
    (archive: boolean) =>
    async (
      filters: DefinedEventFilter[],
      query: string,
      numRows: number,
      lastModifiedBefore: Date,
    ) => {
      try {
        await dispatch(
          AppBackendRequestActions.bulkArchiveDefinedEvents({
            orgId,
            archive,
            filters,
            query,
            lastUpdatedBefore: Timestamp.fromDate(lastModifiedBefore),
          }),
        );
        notifications.success(
          archive
            ? intl.formatMessage(
                {
                  defaultMessage: 'Archived {count, number} Events.',
                  id: '3EAJce',
                },
                { count: numRows },
              )
            : intl.formatMessage(
                {
                  defaultMessage: 'Restored {count, number} Events.',
                  id: 'nLixXh',
                },
                { count: numRows },
              ),
        );
      } catch (e) {
        notifications.error(
          archive
            ? intl.formatMessage({
                defaultMessage: 'Failed to archive Events.',
                id: 'O3jkpC',
              })
            : intl.formatMessage({
                defaultMessage: 'Failed to restore Events.',
                id: 'bVQvOT',
              }),
        );
      }
    };

  const archiveMulti = useCallback(
    async (definedEventIds: string[]) => {
      try {
        if (flags.featureFlags().AppUseMultiArchive && definedEventIds.length > 1) {
          await dispatch(
            DefinedEventsRequestActions.archiveMultiDefinedEvents(orgId, definedEventIds, true),
          );
        } else {
          await Promise.all(
            definedEventIds.map(definedEventId =>
              dispatch(
                DefinedEventsRequestActions.archiveDefinedEventRequest(orgId, definedEventId, true),
              ),
            ),
          );
        }
        notifications.success(
          intl.formatMessage(
            {
              defaultMessage: 'Archived {count, number} Events.',
              id: '3EAJce',
            },
            { count: definedEventIds.length },
          ),
        );
      } catch (e) {
        notifications.error(
          intl.formatMessage({
            defaultMessage: 'Failed to archive Events.',
            id: 'O3jkpC',
          }),
        );
      }
    },
    [flags, notifications, dispatch, orgId],
  );

  const restoreMulti = useCallback(
    async (definedEventIds: string[]) => {
      try {
        if (flags.featureFlags().AppUseMultiArchive && definedEventIds.length > 1) {
          await dispatch(
            DefinedEventsRequestActions.archiveMultiDefinedEvents(orgId, definedEventIds, false),
          );
        } else {
          await Promise.all(
            definedEventIds.map(definedEventId =>
              dispatch(
                DefinedEventsRequestActions.archiveDefinedEventRequest(
                  orgId,
                  definedEventId,
                  false,
                ),
              ),
            ),
          );
        }
        notifications.success(
          intl.formatMessage(
            {
              defaultMessage: 'Restored {count, number} Events.',
              id: 'nLixXh',
            },
            { count: definedEventIds.length },
          ),
        );
      } catch (e) {
        notifications.error(
          intl.formatMessage({
            defaultMessage: 'Failed to restore Events.',
            id: 'bVQvOT',
          }),
        );
      }
    },
    [flags, notifications, dispatch, orgId],
  );

  return {
    save,
    archive,
    unarchive,
    archiveAll: archiveAll(true),
    restoreAll: archiveAll(false),
    archiveMulti,
    restoreMulti,
  };
};
